import React, { useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AuthRoute from 'components/routes/AuthRoute';
import PrivateRoute from 'components/routes/PrivateRoute';
import OnboardingRoute from 'components/routes/OnboardingRoute';

import HomePage from 'features/home/HomePage';
import TeamPage from 'features/team/TeamPage';
import NotFoundPage from 'components/NotFoundPage';
import TourPage from 'features/onboarding/TourPage';
import ClientsPage from 'features/client/ClientsPage';
import WorkersPage from './features/worker/WorkersPage';
import SignInPage from 'features/auth/signIn/SignInPage';
import SignUpPage from 'features/auth/signUp/SignUpPage';
import PartnersPage from 'features/partner/PartnersPage';
import WelcomePage from 'features/onboarding/WelcomePage';
import AccessDeniedPage from 'components/AccessDeniedPage';
import TeammatesPage from 'features/recruiter/TeammatesPage';
import ShowAroundPage from 'features/onboarding/ShowAroundPage';
import RequestsPage from 'features/admin/request/RequestsPage';
import RecruitersPage from 'features/admin/member/RecruitersPage';
import ClientDetailsPage from 'features/client/ClientDetailsPage';
import AssignmentsPage from 'features/assignment/AssignmentsPage';
import WorkerDetailsPage from 'features/worker/WorkerDetailsPage';
import PartnerDetailsPage from 'features/partner/PartnerDetailsPage';
import CompanyProfilePage from 'features/account/CompanyProfilePage';
import ACHPaymentsPage from 'features/admin/achPayments/ACHPaymentsPage';
import PartnerInvitationPage from 'features/partner/PartnerInvitationPage';
import RecruiterDetailsPage from 'features/recruiter/RecruiterDetailsPage';
import ConfirmationPage from 'features/auth/confirmation/ConfirmationPage';
import ResetPasswordPage from 'features/auth/resetPassword/ResetPasswordPage';
import AssignmentDetailsPage from 'features/assignment/AssignmentDetailsPage';
import RegistrationByInvitePage from 'features/auth/RegistrationByInvitePage';
import TermsAndConditions from 'features/termsAndConditions/TermsAndConditions';
import ForgotPasswordPage from 'features/auth/forgotPassword/ForgotPasswordPage';
import WeeklyTrendMixReport from 'features/home/UserHomePage/WeeklyTrendMixReport';
import AgingReportDetailsPage from 'features/home/UserHomePage/AgingReportDetailsPage';
import ProfitReportDetailsPage from 'features/home/UserHomePage/ProfitReportDetailsPage';
import AgingReportSummaryDetailsPage from 'features/home/UserHomePage/AgingReportSummaryDetailsPage';
import AgingReportTotalDetailsPage from 'features/home/UserHomePage/AgingReportTotalDetailsPage';
import MyActiveAssignmentsDetailsPage from 'features/home/UserHomePage/MyActiveAssignmentsDetailsPage';
import TimesheetStatusReportDetailsPage from 'features/home/UserHomePage/TimesheetStatusReportDetailsPage';
import AssignmentsEndingSoonDetailsPage from 'features/home/UserHomePage/AssignmentsEndingSoonDetailsPage';
import MyCustomersDetailsPage from 'features/home/UserHomePage/MyCustomersDetailsPage/MyCustomersDetailsPage';
import WeeklyTrendBillingReportDetailsPage from 'features/home/UserHomePage/WeeklyTrendBillingReportDetailsPage';
import CustomerBillingProfitReportDetailsPage from 'features/home/UserHomePage/CustomerBillingProfitReportDetailsPage';
import CustomerTrendWeeklyProfitReportDetailsPage from 'features/home/UserHomePage/CustomerTrendWeeklyProfitReportDetailsPage';
import AverageRatesCustomerReportDetailsPage from 'features/home/UserHomePage/AverageRatesCustomerReportDetailsPage';
import AgingReportTotalBalancePage from 'features/home/UserHomePage/AgingReportTotalBalancePage';
import AccountSettingsPage from 'features/account/AccountSettingsPage';
import AssignmentTemplateDetailsPage from 'features/assignment/AssignmentTemplateDetailsPage';
import PlacementsDetailsPage from 'features/bulhorn/PlacementsDetailsPage';
import CompanyDetailsPage from 'features/company/CompanyDetailsPage';
import AdminsPage from 'features/admin/admins/AdminsPage';
import { getUserRoles } from 'utils/userRolesUtils';
import PartnerAssignmentDetailsPage from 'features/assignment/PartnerAssignmentDetailsPage';
import AssignmentBulkImportPage from 'features/assignment/AssignmentBulkImportPage';
import ClientBulkImportPage from 'features/client/ClientBulkImportPage';
import ActivationTwoFAPage from 'features/auth/ActivationTwoFAPage';
import MaintenancePage from 'features/maintenance/MaintenancePage';
import MaintenanceAccessPage from 'features/maintenance/MaintenanceAccessPage';
import { MaintenanceModeContext } from 'components/MaintenanceModeProvider';

import { UserRole } from 'enums';

export default function Router() {
  const { isMaintenanceMode } = useContext(MaintenanceModeContext);

  if (isMaintenanceMode) {
    return (
      <Routes>
        <Route
          path="maintenance-access/:code"
          element={<MaintenanceAccessPage />}
        />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="*" element={<Navigate to="maintenance" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AuthRoute />}>
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="login" element={<SignInPage />} />
        <Route path="registration" element={<SignUpPage />} />
        <Route
          path="registration-invite"
          element={<RegistrationByInvitePage />}
        />
      </Route>

      <Route element={<PrivateRoute roles={getUserRoles(UserRole.All)} />}>
        <Route path="/" element={<HomePage />} />
        <Route path="assignments" element={<AssignmentsPage />} />
        <Route
          path="assignment/:assignmentKey"
          element={<AssignmentDetailsPage />}
        />
        <Route
          path="assignment-template/:templateId"
          element={<AssignmentTemplateDetailsPage />}
        />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="partner/:partnerId" element={<PartnerDetailsPage />} />
        <Route path="workers" element={<WorkersPage />} />
        <Route path="worker/:workerKey" element={<WorkerDetailsPage />} />
        <Route path="account-settings" element={<AccountSettingsPage />} />
        <Route
          path="/timesheet-status-report-details"
          element={<TimesheetStatusReportDetailsPage />}
        />
        <Route path="company/:companyId" element={<CompanyDetailsPage />} />
      </Route>

      <Route element={<PrivateRoute roles={getUserRoles(UserRole.Users)} />}>
        <Route path="team" element={<TeamPage />} />
        <Route path="company-profile" element={<CompanyProfilePage />} />
        <Route
          path="my-active-assignments-details"
          element={<MyActiveAssignmentsDetailsPage />}
        />
        <Route
          path="assignments-ending-soon-details"
          element={<AssignmentsEndingSoonDetailsPage />}
        />
        <Route path="show-around" element={<ShowAroundPage />} />
        <Route path="tour" element={<TourPage />} />
        <Route path="placements" element={<PlacementsDetailsPage />} />
      </Route>

      <Route
        element={
          <PrivateRoute
            roles={getUserRoles(UserRole.Users)}
            teammateRoles={getUserRoles(
              UserRole.TeammateSubAdmin,
              UserRole.TeammateAdmin,
            )}
          />
        }
      >
        <Route path="teammates" element={<TeammatesPage />} />
      </Route>

      <Route
        element={
          <PrivateRoute
            roles={getUserRoles(UserRole.Admin, UserRole.RecruiterCompany)}
          />
        }
      >
        <Route path="customers" element={<ClientsPage />} />
        <Route path="customer/:clientKey" element={<ClientDetailsPage />} />
        <Route
          path="assignment-partner/:assignmentKey"
          element={<PartnerAssignmentDetailsPage />}
        />
      </Route>

      <Route
        element={
          <PrivateRoute roles={getUserRoles(UserRole.RecruiterCompany)} />
        }
      >
        <Route
          path="customer-billing-profit-report-details"
          element={<CustomerBillingProfitReportDetailsPage />}
        />
        <Route
          path="customer-trend-weekly-profit-report-details"
          element={<CustomerTrendWeeklyProfitReportDetailsPage />}
        />
        <Route
          path="my-customers-details"
          element={<MyCustomersDetailsPage />}
        />
        <Route
          path="customer-trends-mix-report-details"
          element={<WeeklyTrendMixReport />}
        />
        <Route
          path="average-rates-customer-report-details"
          element={<AverageRatesCustomerReportDetailsPage />}
        />
        <Route
          path="aging-report-details/:customerKey/:period"
          element={<AgingReportDetailsPage />}
        />
        <Route
          path="aging-report-total-balance/:customerKey"
          element={<AgingReportTotalBalancePage />}
        />
        <Route
          path="aging-report-summary-details"
          element={<AgingReportSummaryDetailsPage />}
        />
        <Route
          path="aging-report-total-details/:period"
          element={<AgingReportTotalDetailsPage />}
        />
        <Route
          path="profit-report-details"
          element={<ProfitReportDetailsPage />}
        />
        <Route
          path="weekly-trend-billing-report-details"
          element={<WeeklyTrendBillingReportDetailsPage />}
        />
      </Route>

      <Route element={<PrivateRoute roles={getUserRoles(UserRole.Admin)} />}>
        <Route path="todo" element={<RequestsPage />} />
        <Route path="members" element={<RecruitersPage />} />
        <Route path="member/:memberKey" element={<RecruiterDetailsPage />} />
        <Route path="ach-payments" element={<ACHPaymentsPage />} />
        <Route path="admins" element={<AdminsPage />} />
        <Route
          path="assignment-bulk-import"
          element={<AssignmentBulkImportPage />}
        />
        <Route path="customer-bulk-import" element={<ClientBulkImportPage />} />
      </Route>

      <Route path="two-fa-activation" element={<ActivationTwoFAPage />} />

      <Route element={<OnboardingRoute />}>
        <Route path="welcome" element={<WelcomePage />} />
      </Route>

      <Route path="terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="confirmation" element={<ConfirmationPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route
        path="partner-invitation/:invitationId"
        element={<PartnerInvitationPage />}
      />
      <Route path="blank-page" />
      <Route path="access-denied" element={<AccessDeniedPage />} />
      <Route path="not-found" element={<NotFoundPage />} />

      <Route path="maintenance" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="not-found" replace />} />
    </Routes>
  );
}
